import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { CacheKeys } from '@/lib/cache'
import {
  FILL_STATE_POLL_MS,
  fillApplicationError,
  fillApplicationErrorMessage,
  type FillApplicationState,
  type FillCard,
  FillErrorCode,
  invalidateFillApplication,
  submitApplication,
} from '@/models/fillApplication'
import { FillApplicationEvent, trackFillApplication } from './fillApplicationAnalytics'
import { focusFillField } from './focusFillField'

/** How long Send stays busy behind a send already under way; longer than a dispatch takes. */
export const SEND_IN_PROGRESS_WAIT_MS = 90_000

export interface SendableCard {
  card: FillCard
  label: string
  isDirty: boolean
  save: () => Promise<boolean>
}

interface SendOptions {
  /** Brings a card's tab up, so a card that didn't save is on screen. */
  showCard?: (card: FillCard) => void
}

export function useSendApplication(orgId: string, cards: SendableCard[], { showCard }: SendOptions = {}) {
  const queryClient = useQueryClient()
  const [sending, setSending] = useState(false)
  // Another send of this application holds the claim (another tab, another rep): wait for it to land.
  const [otherSendRunning, setOtherSendRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Bumped on each refusal for missing items; the footer opens its list and focuses it.
  const [blockedNonce, setBlockedNonce] = useState(0)

  useEffect(() => {
    if (!otherSendRunning) return
    const stateKey = [CacheKeys.FILL_APPLICATION_STATE, orgId]
    const poll = window.setInterval(() => {
      if (queryClient.getQueryData<FillApplicationState>(stateKey)?.sent) {
        setOtherSendRunning(false)
        return
      }
      void queryClient.invalidateQueries({ queryKey: stateKey })
    }, FILL_STATE_POLL_MS)
    const giveUp = window.setTimeout(() => {
      setOtherSendRunning(false)
      setError(null)
    }, SEND_IN_PROGRESS_WAIT_MS)
    return () => {
      window.clearInterval(poll)
      window.clearTimeout(giveUp)
    }
  }, [otherSendRunning, orgId, queryClient])

  async function send() {
    if (otherSendRunning) return
    setSending(true)
    setError(null)
    try {
      for (const card of cards) {
        if (!card.isDirty) continue
        if (!(await card.save())) {
          setError(`The ${card.label} tab didn't save. Fix it, then send again.`)
          showCard?.(card.card)
          window.setTimeout(() => focusFillField('', card.card), 0)
          return
        }
      }
      const state = await submitApplication(orgId)
      queryClient.setQueryData([CacheKeys.FILL_APPLICATION_STATE, orgId], state)
      trackFillApplication(FillApplicationEvent.SENT, {
        org_id: orgId,
        application_id: state.application?.id,
      })
      toast.success('Sent to carriers')
      await invalidateFillApplication(queryClient, orgId, { state: false })
    } catch (sendError) {
      const refusal = fillApplicationError(sendError)
      if (refusal?.code === FillErrorCode.CHECKS_FAILED) {
        trackFillApplication(FillApplicationEvent.SEND_BLOCKED, {
          org_id: orgId,
          missing: (refusal.checks ?? []).map((check) => check.label),
        })
        setBlockedNonce((count) => count + 1)
        toast.error("Not sent. Fix what's listed above, then send again.")
      } else if (refusal?.code === FillErrorCode.SEND_IN_PROGRESS) {
        setError(refusal.detail)
        setOtherSendRunning(true)
      } else {
        setError(fillApplicationErrorMessage(sendError))
      }
      // Fresh checks for the list, or the sent state when that's why it was refused.
      await invalidateFillApplication(queryClient, orgId)
    } finally {
      setSending(false)
    }
  }

  return { send, sending: sending || otherSendRunning, error, blockedNonce }
}
